import { useState, useEffect, useRef, useCallback } from 'react';

interface UnifiedProgressOptions {
  /**
   * Duration to reach 95% (in milliseconds)
   * Default: 27000ms (27 seconds)
   */
  phase1Duration?: number;
  /**
   * Duration to go from 95% to 99% (in milliseconds)
   * Default: 15000ms (15 seconds)
   */
  phase2Duration?: number;
}

/**
 * Hook that provides a unified progress bar with non-linear progression:
 * - Phase 1: 0% → 95% over 27 seconds (slow start, then accelerates)
 * - Phase 2: 95% → 99% over 15 seconds (slow progression)
 * - Can be dismissed at any time when actual work is complete
 */
export function useUnifiedProgress(options: UnifiedProgressOptions = {}) {
  const { phase1Duration = 27000, phase2Duration = 15000 } = options;

  const [progress, setProgress] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const startTimeRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  /**
   * Easing function for slow start then acceleration (ease-in-quad)
   * Progress curve: starts slow, accelerates toward the end
   */
  const easeInQuad = useCallback((t: number): number => {
    return t * t;
  }, []);

  /**
   * Easing function for slow progression (ease-out-cubic)
   * Very slow progression for 95% → 99%
   */
  const easeOutCubic = useCallback((t: number): number => {
    return 1 - Math.pow(1 - t, 3);
  }, []);

  const updateProgress = useCallback(() => {
    if (!startTimeRef.current) return;

    const elapsed = Date.now() - startTimeRef.current;

    // Phase 1: 0% → 95% over phase1Duration (27 seconds)
    if (elapsed < phase1Duration) {
      const t = elapsed / phase1Duration;
      const easedProgress = easeInQuad(t);
      const currentProgress = easedProgress * 95;
      setProgress(currentProgress);
      animationFrameRef.current = requestAnimationFrame(updateProgress);
    }
    // Phase 2: 95% → 99% over phase2Duration (15 seconds)
    else if (elapsed < phase1Duration + phase2Duration) {
      const phase2Elapsed = elapsed - phase1Duration;
      const t = phase2Elapsed / phase2Duration;
      const easedProgress = easeOutCubic(t);
      const currentProgress = 95 + easedProgress * 4; // 95% + 4% = 99%
      setProgress(currentProgress);
      animationFrameRef.current = requestAnimationFrame(updateProgress);
    }
    // Stay at 99% after phase2Duration
    else {
      setProgress(99);
    }
  }, [phase1Duration, phase2Duration, easeInQuad, easeOutCubic]);

  const start = useCallback(() => {
    if (isActive) return; // Prevent multiple starts

    setIsActive(true);
    setProgress(0);
    startTimeRef.current = Date.now();
    animationFrameRef.current = requestAnimationFrame(updateProgress);
  }, [isActive, updateProgress]);

  const reset = useCallback(() => {
    setIsActive(false);
    setProgress(0);
    startTimeRef.current = null;

    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  const complete = useCallback(() => {
    setProgress(100);
    setIsActive(false);

    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return {
    progress: Math.round(progress),
    isActive,
    start,
    reset,
    complete,
  };
}
